// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
/**
 * PostLoadSummary.jsx
 *
 * data-fit LLC — DecisionFlow
 * Stage 1 LOAD — Screen LOAD-03: Post-Load Summary
 * Branch: feature/compliance-lifecycle-foundation
 *
 * Confirmation screen after successful ingestion. Fetches batch summary
 * via GET, then fires Scope Advisor AI (sequential — not parallel).
 * Download of full load report is generated client-side from GET response.
 * No backend endpoint for download.
 *
 * Fix applied (F1): postScopeAdvisorQuery now calls onAiResponseReceived(payload)
 * instead of dispatching a CustomEvent, so parent aiResponse state updates
 * correctly and ScopeAdvisorPanel renders Scope Advisor content.
 *
 * Spec authority: ComponentSpec_LOAD_corrected_S86g.md
 * Path: ui/src/components/tenant-admin/PostLoadSummary.jsx
 */


import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';

const API_BASE_URL = '/api/v1';
const AI_TIMEOUT_MS = 10000;

const TABLE_LABELS = {
  fiscal_year: 'Fiscal years',
  fiscal_period: 'Fiscal periods',
  business_unit: 'Business units',
  segment: 'Segments',
  role: 'Roles',
  selling_motion: 'Selling motions',
  position: 'Positions',
  user_account: 'Comp admins',
  user_role_assignment: 'Role assignments',
  exchange_rate: 'FX rates',
  region: 'Regions',
  sub_region: 'Sub-regions',
};

const TIER_TABLES = {
  essentials: [
    'fiscal_year',
    'fiscal_period',
    'business_unit',
    'segment',
    'role',
    'selling_motion',
    'position',
  ],
  standard: [
    'fiscal_year',
    'fiscal_period',
    'business_unit',
    'segment',
    'role',
    'selling_motion',
    'position',
    'user_account',
    'user_role_assignment',
  ],
  enterprise: [
    'fiscal_year',
    'fiscal_period',
    'business_unit',
    'segment',
    'role',
    'selling_motion',
    'position',
    'user_account',
    'user_role_assignment',
    'exchange_rate',
    'region',
    'sub_region',
  ],
};

function normalizeTier(tier) {
  return String(tier || '').toLowerCase();
}

function timeoutPromise(ms) {
  return new Promise((_, reject) => {
    window.setTimeout(() => reject(new Error('AI_TIMEOUT')), ms);
  });
}

function escapeCsv(value) {
  const normalized = value === null || value === undefined ? '' : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
}

function downloadTextFile({ filename, content }) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="skeleton h-28 rounded-3xl" />
      <div className="grid gap-4 md:grid-cols-3">
        <div className="skeleton h-28 rounded-3xl" />
        <div className="skeleton h-28 rounded-3xl" />
        <div className="skeleton h-28 rounded-3xl" />
      </div>
    </div>
  );
}

function MetricCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 shadow-sm">
      <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">{label}</p>
      <p className="mt-2 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">{value}</p>
      {note && <p className="mt-2 text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">{note}</p>}
    </div>
  );
}

function ReloadModal({ onCancel, onContinue }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[rgb(var(--color-text))]/50 px-4">
      <div className="w-full max-w-xl rounded-3xl bg-[rgb(var(--color-surface))] p-6 shadow-2xl">
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Re-load data?</h2>
        <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">
          This tenant is already initialised. Re-loading will overwrite all reference data. Any configurations made in
          Stage 2 or beyond will be lost. This action is blocked once fiscal periods are activated.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="rounded-xl bg-[rgb(var(--color-danger))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-danger))]/80 focus:outline-none"
          >
            Continue to Re-load
          </button>
        </div>
      </div>
    </div>
  );
}

function ScopeAdvisorPanel({ aiStatus, aiResponse, onRetry }) {
  const response = aiStatus === 'ready' ? aiResponse?.ai_response : null;
  const recommendations = Array.isArray(response?.recommendations) ? response.recommendations : [];
  const domains = Array.isArray(response?.domains) ? response.domains : [];

  return (
    <aside className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
      <div className="mb-4">
        <p className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">Scope Advisor</p>
        <h2 className="mt-1 text-tok-lg font-semibold text-[rgb(var(--color-text))]">Active</h2>
      </div>

      {aiStatus === 'loading' && (
        <div className="space-y-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[rgb(var(--color-border))] border-t-[rgb(var(--color-text))]" />
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Reviewing the loaded org structure for Stage 2 setup guidance.</p>
        </div>
      )}

      {aiStatus === 'error' && (
        <div className="space-y-3 rounded-2xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-4">
          <p className="text-tok-sm text-[rgb(var(--color-danger))]">AI guidance could not be generated.</p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            Retry
          </button>
        </div>
      )}

      {aiStatus === 'timeout' && (
        <div className="space-y-3 rounded-2xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-4">
          <p className="text-tok-sm text-[rgb(var(--color-warning))]">AI guidance timed out. You can retry or continue to Configure.</p>
          <button
            type="button"
            onClick={onRetry}
            className="rounded-xl bg-[rgb(var(--color-primary))] px-3 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
          >
            Retry
          </button>
        </div>
      )}

      {aiStatus === 'blocked' && (
        <div className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">AI guidance is unavailable for this request.</p>
        </div>
      )}

      {aiStatus === 'ready' && (
        <div className="space-y-4">
          {response?.summary && <p className="text-tok-sm leading-6 text-[rgb(var(--color-text))]">{response.summary}</p>}

          {recommendations.length > 0 && (
            <div className="space-y-3">
              {recommendations.map((recommendation, index) => (
                <div key={`${recommendation.title}-${index}`} className="rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
                  <span className="rounded-full bg-[rgb(var(--color-surface))] px-2.5 py-1 text-tok-xs font-semibold capitalize text-[rgb(var(--color-text-muted))] ring-1 ring-[rgb(var(--color-border))]">
                    {recommendation.severity}
                  </span>
                  <h3 className="mt-3 text-tok-sm font-semibold text-[rgb(var(--color-text))]">{recommendation.title}</h3>
                  <p className="mt-1 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">{recommendation.detail}</p>
                </div>
              ))}
            </div>
          )}

          {domains.length > 0 && (
            <button
              type="button"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
            >
              Preview domains
            </button>
          )}
        </div>
      )}

      {(aiStatus === 'idle' || !aiStatus) && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Scope Advisor guidance will appear after the load summary is fetched.</p>
      )}
    </aside>
  );
}

export default function PostLoadSummary({
  bootstrapBatchId,
  tier,
  aiResponse,
  aiStatus,
  onProceedToConfigure,
  onBackToReload,
  onAiStatusChange,
  onAiResponseReceived,
}) {
  const { token, user } = useAuth();

  const [batchSummary, setBatchSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReloadModal, setShowReloadModal] = useState(false);

  const tierTables = useMemo(() => TIER_TABLES[normalizeTier(tier)] || TIER_TABLES.essentials, [tier]);

  const rejectedRows = useMemo(() => {
    return (batchSummary?.files || []).reduce((total, file) => total + (file.rows_rejected || 0), 0);
  }, [batchSummary]);

  const visibleTables = useMemo(() => {
    const tablesPopulated = batchSummary?.tables_populated || {};

    return tierTables
      .filter((tableName) => Number(tablesPopulated[tableName] || 0) > 0)
      .map((tableName) => ({
        tableName,
        label: TABLE_LABELS[tableName] || tableName,
        value: tablesPopulated[tableName],
      }));
  }, [batchSummary, tierTables]);

  const postScopeAdvisorQuery = useCallback(
    async (summary) => {
      const tables = summary?.tables_populated || {};

      onAiStatusChange('loading');

      try {
        const response = await Promise.race([
          fetch(`${API_BASE_URL}/ai-queries`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              panel_context: 'configure.scope_advisor',
              input_data: {
                tenant_id: summary?.tenant_id || user?.tenant_id,
                tier,
                org_summary: {
                  business_unit_count: Number(tables.business_unit || 0),
                  segment_count: Number(tables.segment || 0),
                  position_count: Number(tables.position || 0),
                  comp_admin_count: Number(tables.user_account || 0),
                },
              },
            }),
          }),
          timeoutPromise(AI_TIMEOUT_MS),
        ]);

        if (!response.ok) {
          onAiStatusChange('error');
          return;
        }

        const payload = await response.json();

        if (payload.query_blocked === true) {
          onAiStatusChange('blocked');
          return;
        }

        if (onAiResponseReceived) {
          onAiResponseReceived(payload);
          // onAiResponseReceived sets aiStatus='ready' in parent (handleAiResponseReceived)
        } else {
          onAiStatusChange('ready');
        }
      } catch (requestError) {
        onAiStatusChange(requestError.message === 'AI_TIMEOUT' ? 'timeout' : 'error');
      }
    },
    [onAiResponseReceived, onAiStatusChange, tier, token, user]
  );

  const fetchBatchSummary = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/bootstrap-import/${bootstrapBatchId}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        let payload = {};
        try {
          payload = await response.json();
        } catch {
          payload = {};
        }

        if (payload.code === 'AUTH_INSUFFICIENT_ROLE') {
          setError({
            blocking: true,
            message: 'You do not have permission to view this load summary.',
          });
          return;
        }

        setError({
          retryable: true,
          message: 'Unable to load summary. Your data was loaded successfully.',
        });
        return;
      }

      const payload = await response.json();
      setBatchSummary(payload);
      setIsLoading(false);

      postScopeAdvisorQuery(payload);
    } catch {
      setError({
        retryable: true,
        message: 'Unable to load summary. Your data was loaded successfully.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [bootstrapBatchId, postScopeAdvisorQuery, token]);

  useEffect(() => {
    if (!bootstrapBatchId) {
      setError({
        blocking: true,
        message: 'Batch ID is missing. Return to upload and re-validate.',
      });
      setIsLoading(false);
      return;
    }

    fetchBatchSummary();
  }, [bootstrapBatchId, fetchBatchSummary]);

  const handleDownloadReport = useCallback(() => {
    if (!batchSummary) return;

    const header = [
      'file_type',
      'source_filename',
      'load_status',
      'rows_received',
      'rows_loaded',
      'rows_rejected',
      'warnings_count',
      'loaded_at',
      'loaded_by',
    ];

    const rows = (batchSummary.files || []).map((file) =>
      [
        file.file_type,
        file.source_filename,
        file.load_status,
        file.rows_received,
        file.rows_loaded,
        file.rows_rejected,
        batchSummary.warnings_count,
        batchSummary.loaded_at,
        batchSummary.loaded_by,
      ]
        .map(escapeCsv)
        .join(',')
    );

    downloadTextFile({
      filename: `load_report_${bootstrapBatchId}.csv`,
      content: [header.join(','), ...rows].join('\n'),
    });
  }, [batchSummary, bootstrapBatchId]);

  const canProceed =
    !batchSummary ||
    batchSummary.load_status === 'loaded' ||
    batchSummary.load_status === 'partial' ||
    error?.retryable;

  if (error?.blocking) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="max-w-xl rounded-3xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-surface))] p-6 shadow-sm">
          <h1 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Unable to continue</h1>
          <p className="mt-3 text-tok-sm leading-6 text-[rgb(var(--color-text-muted))]">{error.message}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-7xl flex-col px-6 py-6">
      {showReloadModal && (
        <ReloadModal
          onCancel={() => setShowReloadModal(false)}
          onContinue={() => {
            setShowReloadModal(false);
            onBackToReload();
          }}
        />
      )}

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4 rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
        <div>
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text-muted))]">{user?.tenant_name || batchSummary?.tenant_id || 'Tenant'}</p>
          <h1 className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Stage 1 — Load Complete</h1>
          <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
            {batchSummary?.loaded_at || batchSummary?.validated_at || 'Load timestamp unavailable'} · Batch {bootstrapBatchId}
          </p>
        </div>
        <span className="rounded-full bg-[rgb(var(--color-success))]/10 px-3 py-1 text-tok-sm font-semibold text-[rgb(var(--color-success))] ring-1 ring-[rgb(var(--color-success))]/30">
          ✓ Success
        </span>
      </header>

      {isLoading ? (
        <Skeleton />
      ) : error?.retryable ? (
        <section className="rounded-3xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-6 shadow-sm">
          <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-warning))]">{error.message}</h2>
          <p className="mt-2 text-tok-sm text-[rgb(var(--color-warning))]">
            You can retry fetching the summary or proceed to Configure. Configure will fetch its own data independently.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={fetchBatchSummary}
              className="rounded-xl bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] focus:outline-none"
            >
              Retry
            </button>
            <button
              type="button"
              onClick={onProceedToConfigure}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
            >
              Proceed anyway
            </button>
          </div>
        </section>
      ) : (
        <div className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <section className="space-y-5">
            <div className="rounded-3xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 p-5 shadow-sm">
              <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-success))]">✓ Bootstrap load completed</h2>
              <p className="mt-2 text-tok-sm leading-6 text-[rgb(var(--color-success))]">
                Fiscal periods are loaded but not yet activated. Activate in Stage 2 — Configure.
              </p>
            </div>

            {batchSummary?.load_status === 'partial' && (
              <div className="rounded-3xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-5 shadow-sm">
                <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-warning))]">Some rows were not loaded</h2>
                <p className="mt-2 text-tok-sm leading-6 text-[rgb(var(--color-warning))]">
                  {rejectedRows} rows were rejected across one or more files. Download the load report to review rejected rows.
                  You may proceed to Configure — your org structure is partially loaded.
                </p>
              </div>
            )}

            {batchSummary?.load_status !== 'loaded' && batchSummary?.load_status !== 'partial' && (
              <div className="rounded-3xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 p-5 shadow-sm">
                <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-danger))]">Load completed with errors</h2>
                <p className="mt-2 text-tok-sm leading-6 text-[rgb(var(--color-danger))]">
                  Contact support with batch ID {bootstrapBatchId}.
                </p>
              </div>
            )}

            {batchSummary?.warnings_count > 0 && (
              <div className="rounded-3xl border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/10 p-5 shadow-sm">
                <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-warning))]">Warnings carried forward</h2>
                <p className="mt-2 text-tok-sm leading-6 text-[rgb(var(--color-warning))]">
                  {batchSummary.warnings_count} warnings were acknowledged during load. Review them in Stage 2 — Configure.
                </p>
              </div>
            )}

            <section className="rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-tok-lg font-semibold text-[rgb(var(--color-text))]">What was created</h2>
                  <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">One card per populated table from the load summary.</p>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadReport}
                  className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
                >
                  Download full load report ↓
                </button>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {visibleTables.map((table) => (
                  <MetricCard
                    key={table.tableName}
                    label={table.label}
                    value={table.value}
                    note={table.tableName === 'fiscal_period' ? '◑ inactive until Stage 2 activation' : null}
                  />
                ))}
              </div>
            </section>
          </section>

          <ScopeAdvisorPanel
            aiStatus={aiStatus}
            aiResponse={aiResponse}
            onRetry={() => {
              if (batchSummary) {
                postScopeAdvisorQuery(batchSummary);
              }
            }}
          />
        </div>
      )}

      <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 shadow-sm">
        <button
          type="button"
          onClick={() => setShowReloadModal(true)}
          className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-semibold text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] focus:outline-none"
        >
          ← Re-load Data
        </button>

        <button
          type="button"
          disabled={!canProceed || (batchSummary && batchSummary.load_status !== 'loaded' && batchSummary.load_status !== 'partial')}
          onClick={onProceedToConfigure}
          className="rounded-xl bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-semibold text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:cursor-not-allowed disabled:bg-[rgb(var(--color-disabled))] focus:outline-none"
        >
          Proceed to Configure →
        </button>
      </footer>
    </main>
  );
}
